import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useTutorial } from '../contexts/TutorialContext';
import { getTranslation } from '../languages';
import { 
    ArrowRight, ArrowLeft, X, CheckCircle, Plus
} from 'lucide-react';

const TutorialMode = ({ uiLanguage = 'en', onAddTopicClick }) => {
    const {
        isTutorialMode,
        currentStep,
        currentFlow,
        isPaused,
        tutorialFlows,
        getCurrentStepData,
        getTutorialProgress,
        nextStep,
        previousStep,
        exitTutorial,
        pauseTutorial,
        resumeTutorial,
        skipTutorial
    } = useTutorial();

    const [spotlightPosition, setSpotlightPosition] = useState(null);
    const [multipleSpotlights, setMultipleSpotlights] = useState([]);
    const [transitionSpotlights, setTransitionSpotlights] = useState([]); // For smooth split/merge animations
    const [isAnimatingSpotlights, setIsAnimatingSpotlights] = useState(false);
    const [showContent, setShowContent] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [dummyContent, setDummyContent] = useState(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [contentPosition, setContentPosition] = useState(null);
    const tutorialRef = useRef(null);
    const previousStepRef = useRef(null);
    const animationTimeoutRef = useRef(null);
    const spotlightPositionRef = useRef(null);
    const multipleSpotlightsRef = useRef([]);
    
    // Keep refs in sync with state
    spotlightPositionRef.current = spotlightPosition;
    multipleSpotlightsRef.current = multipleSpotlights;
    
    const t = useCallback((key) => getTranslation(key, uiLanguage), [uiLanguage]);


    const generateDummyContent = useCallback((stepData) => {
        if (!stepData) return null;

        switch (stepData.id) {
            case 'topic-card':
                return null; // No dummy content card needed, we'll show the legend instead
            
            case 'add-tip':
                return null; // No dummy content needed for add tip button
            
            case 'tip-card':
                return null; // Will be shown with actual dummy tips in the UI
            
            case 'tip-content':
                return null; // The actual tip content is already shown in the main area
            
            case 'ai-tutor':
                return {
                    type: 'conversation',
                    data: {
                        messages: []
                    }
                };
            
            case 'review-system':
                return {
                    type: 'review',
                    data: {
                        question: t('tutorialDummyReviewQuestion') || 'What is the difference between let and const?',
                        options: [
                            t('tutorialDummyOption1') || 'let can be reassigned, const cannot',
                            t('tutorialDummyOption2') || 'const can be reassigned, let cannot',
                            t('tutorialDummyOption3') || 'Both can be reassigned',
                            t('tutorialDummyOption4') || 'Neither can be reassigned'
                        ],
                        correctAnswer: 0,
                        explanation: t('tutorialDummyExplanation') || 'Variables declared with let can be reassigned, while const creates a constant reference.'
                    }
                };
            
            default:
                return null;
        }
    }, [t]);

    const handleExit = useCallback(() => {
        setIsExiting(true);
        setShowContent(false);
        setTimeout(() => {
            setIsExiting(false);
            exitTutorial();
        }, 300);
    }, [exitTutorial]);

    const handleNextStep = useCallback(() => {
        // Apply fade transition to all steps
        setIsTransitioning(true);
        setShowContent(false);
        setTimeout(() => {
            nextStep();
        }, 200);
    }, [nextStep]);

    const handlePreviousStep = useCallback(() => {
        // Apply fade transition to all steps
        setIsTransitioning(true);
        setShowContent(false);
        setTimeout(() => {
            previousStep();
        }, 200);
    }, [previousStep]);

    useEffect(() => {
        if (isTutorialMode && !isPaused) {
            const stepData = getCurrentStepData();
            
            if (stepData) {
                // Debug log for backdrop effect analysis (remove after testing)
                if (stepData.id === 'review-modal' || stepData.id === 'new-concept-modal' || 
                    stepData.id === 'scheduled-generation' || stepData.id === 'ai-learning-summary') {
                    console.log('[TutorialMode Debug] Modal step activated:', stepData.id, 
                               '- This modal should have its own backdrop plus tutorial overlay');
                }
                
                // Apply fade transition to all steps
                if (previousStepRef.current && previousStepRef.current.id !== stepData.id) {
                    setIsTransitioning(true);
                    setShowContent(false);
                }
                
                setDummyContent(generateDummyContent(stepData));
                
                if ((stepData.type === 'spotlight' || (stepData.type === 'modal' && (stepData.id === 'new-concept-modal' || stepData.id === 'review-modal' || stepData.id === 'scheduled-generation' || stepData.id === 'ai-learning-summary'))) && stepData.target) {
                    // Reduce delay for modals to improve performance
                    const delay = (stepData.id === 'new-concept-modal' || stepData.id === 'review-modal' || stepData.id === 'scheduled-generation' || stepData.id === 'ai-learning-summary') ? 150 : 100;
                    
                    // Capture the actual previous step before the timer
                    const actualPreviousStep = previousStepRef.current;
                    
                    const timer = setTimeout(() => {
                        // Handle multiple targets for filter-functions step
                        const targets = stepData.target.split(',').map(t => t.trim());
                        const allTargetElements = [];
                        const spotlights = [];
                        
                        // Collect elements for each target
                        targets.forEach(target => {
                            const elements = document.querySelectorAll(`[data-tutorial-target="${target}"]`);
                            if (elements.length > 0) {
                                allTargetElements.push(...elements);
                                
                                // For filter-functions, create separate spotlights
                                if (stepData.id === 'filter-functions') {
                                    // For filter-functions, we want to highlight ONLY the filter buttons
                                    // The container div contains the buttons, but we need their exact bounds
                                    let minTop = Infinity, minLeft = Infinity;
                                    let maxBottom = -Infinity, maxRight = -Infinity;
                                    let buttonCount = 0;
                                    
                                    elements.forEach((element) => {
                                        
                                        // Get only direct child buttons or buttons in direct child divs
                                        const filterButtons = element.querySelectorAll(':scope > div > button, :scope > button');
                                        
                                        filterButtons.forEach((button, btnIdx) => {
                                            const btnRect = button.getBoundingClientRect();
                                            const scrollY = window.scrollY;
                                            
                                            // Check if button has a specific icon to identify it
                                            // Look for the icon using various possible selectors
                                            const svgIcon = button.querySelector('svg');
                                            const svgClasses = svgIcon ? svgIcon.getAttribute('class') || '' : '';
                                            const hasPlus = svgClasses.includes('lucide-plus') || button.innerHTML.includes('Plus');
                                            
                                            // Skip if it's a Plus button (shouldn't happen but just in case)
                                            if (hasPlus) {
                                                return;
                                            }
                                            
                                            minTop = Math.min(minTop, btnRect.top + scrollY);
                                            minLeft = Math.min(minLeft, btnRect.left);
                                            maxBottom = Math.max(maxBottom, btnRect.bottom + scrollY);
                                            maxRight = Math.max(maxRight, btnRect.right);
                                            buttonCount++;
                                        });
                                    });
                                    
                                    if (buttonCount > 0) {
                                        const spotlight = {
                                            top: minTop,
                                            left: minLeft,
                                            width: maxRight - minLeft,
                                            height: maxBottom - minTop,
                                            centerX: minLeft + (maxRight - minLeft) / 2,
                                            centerY: minTop + (maxBottom - minTop) / 2
                                        };
                                        spotlights.push(spotlight);
                                    }
                                }
                            }
                        });
                        
                        const targetElements = allTargetElements;
                        
                        if (targetElements.length > 0) {
                            // Force reflow for modal positioning
                            if (stepData.id === 'new-concept-modal' || stepData.id === 'review-modal' || stepData.id === 'scheduled-generation' || stepData.id === 'ai-learning-summary') {
                                // Access offsetHeight to force reflow
                                // eslint-disable-next-line no-unused-expressions
                                targetElements[0].offsetHeight;
                            }
                            
                            let newSpotlightPos = null;
                            
                            // For filter-functions, don't calculate overall bounding box from containers
                            // Use the custom spotlights we already calculated
                            if (stepData.id === 'filter-functions' && spotlights.length > 0) {
                                // Create a combined bounding box from the custom spotlights
                                let minTop = Infinity, minLeft = Infinity;
                                let maxBottom = -Infinity, maxRight = -Infinity;
                                
                                spotlights.forEach(spotlight => {
                                    minTop = Math.min(minTop, spotlight.top);
                                    minLeft = Math.min(minLeft, spotlight.left);
                                    maxBottom = Math.max(maxBottom, spotlight.top + spotlight.height);
                                    maxRight = Math.max(maxRight, spotlight.left + spotlight.width);
                                });
                                
                                newSpotlightPos = {
                                    top: minTop,
                                    left: minLeft,
                                    width: maxRight - minLeft,
                                    height: maxBottom - minTop,
                                    centerX: minLeft + (maxRight - minLeft) / 2,
                                    centerY: minTop + (maxBottom - minTop) / 2
                                };
                            } else {
                                // Calculate bounding box that includes all target elements
                                let minTop = Infinity, minLeft = Infinity;
                                let maxBottom = -Infinity, maxRight = -Infinity;
                                
                                targetElements.forEach((element) => {
                                    const rect = element.getBoundingClientRect();
                                    const scrollY = window.scrollY;
                                    
                                    minTop = Math.min(minTop, rect.top + scrollY);
                                    minLeft = Math.min(minLeft, rect.left);
                                    maxBottom = Math.max(maxBottom, rect.bottom + scrollY);
                                    maxRight = Math.max(maxRight, rect.right);
                                });
                                
                                const width = maxRight - minLeft;
                                const height = maxBottom - minTop;
                                
                                newSpotlightPos = {
                                    top: minTop,
                                    left: minLeft,
                                    width: width,
                                    height: height,
                                    centerX: minLeft + width / 2,
                                    centerY: minTop + height / 2
                                };
                            }
                            
                            // Handle spotlight transitions with animation
                            const previousStepId = actualPreviousStep?.id;
                            const isTransitioningToFilters = stepData.id === 'filter-functions' && previousStepId !== 'filter-functions';
                            const isTransitioningFromFilters = previousStepId === 'filter-functions' && stepData.id !== 'filter-functions';
                            
                            if (stepData.id === 'filter-functions' && spotlights.length > 0) {
                                if (isTransitioningToFilters) {
                                    // Animate split: Start with single spotlight, then split to two
                                    setIsAnimatingSpotlights(true);
                                    
                                    // Clear any existing spotlights
                                    setSpotlightPosition(null);
                                    setMultipleSpotlights([]);
                                    
                                    // Start with transition spotlights at the previous position
                                    const startPos = spotlightPositionRef.current || newSpotlightPos;
                                    
                                    // Create two spotlights at the same starting position with unique IDs
                                    const initialSpotlights = [
                                        { ...startPos, targetIndex: 0, id: 'split-0' },
                                        { ...startPos, targetIndex: 1, id: 'split-1' }
                                    ];
                                    setTransitionSpotlights(initialSpotlights);
                                    
                                    // Clear animation timeout if exists
                                    if (animationTimeoutRef.current) {
                                        clearTimeout(animationTimeoutRef.current);
                                    }
                                    
                                    // Force React to render the initial state immediately
                                    setTimeout(() => {
                                        // Now move them to their target positions with IDs preserved
                                        flushSync(() => {
                                            setTransitionSpotlights(spotlights.map((spot, idx) => ({ ...spot, targetIndex: idx, id: `split-${idx}` })));
                                        });
                                        
                                        // After animation completes, switch to regular multiple spotlights
                                        animationTimeoutRef.current = setTimeout(() => {
                                            setMultipleSpotlights(spotlights);
                                            setTransitionSpotlights([]);
                                            setIsAnimatingSpotlights(false);
                                        }, 500); // Match CSS transition duration
                                    }, 100); // Give React time to render initial state
                                } else {
                                    // Regular set (no animation needed)
                                    setMultipleSpotlights(spotlights);
                                    setSpotlightPosition(null);
                                }
                                
                                // Position content based on multiple spotlights
                                const rightSpotlight = spotlights.length > 1 ? spotlights[1] : spotlights[0];
                                const leftSpotlight = spotlights[0];
                                
                                const newContentPos = {
                                    top: Math.max(leftSpotlight.top + leftSpotlight.height + 30, rightSpotlight.top + rightSpotlight.height + 30),
                                    left: Math.min(rightSpotlight.left + rightSpotlight.width + 30, window.innerWidth - 520)
                                };
                                
                                setContentPosition(newContentPos);
                            } else {
                                if (isTransitioningFromFilters && multipleSpotlightsRef.current.length > 0) {
                                    // Animate merge: Start with two spotlights, merge to one
                                    setIsAnimatingSpotlights(true);
                                    
                                    // Clear any existing spotlights
                                    setSpotlightPosition(null);
                                    setMultipleSpotlights([]);
                                    
                                    // Start with current multiple spotlights at their positions with unique IDs
                                    const initialSpotlights = multipleSpotlightsRef.current.map((spot, idx) => ({ ...spot, targetIndex: idx, id: `merge-${idx}` }));
                                    setTransitionSpotlights(initialSpotlights);
                                    
                                    // Clear animation timeout if exists
                                    if (animationTimeoutRef.current) {
                                        clearTimeout(animationTimeoutRef.current);
                                    }
                                    
                                    // Force React to render the initial state immediately
                                    setTimeout(() => {
                                        // Now move them to the target position
                                        flushSync(() => {
                                            setTransitionSpotlights([
                                                { ...newSpotlightPos, targetIndex: 0, id: 'merge-0' },
                                                { ...newSpotlightPos, targetIndex: 1, id: 'merge-1' }
                                            ]);
                                        });
                                        
                                        // After animation completes, switch to single spotlight
                                        animationTimeoutRef.current = setTimeout(() => {
                                            setSpotlightPosition(newSpotlightPos);
                                            setTransitionSpotlights([]);
                                            setIsAnimatingSpotlights(false);
                                        }, 500); // Match CSS transition duration
                                    }, 100); // Give React time to render initial state
                                } else {
                                    // Regular set (no animation needed)
                                    setSpotlightPosition(newSpotlightPos);
                                    setMultipleSpotlights([]);
                                }
                                
                                // Calculate content position based on step
                                // For AI tutor and quick actions, position content just above spotlight
                                // Account for spotlight padding (10px) and border (3px) = 13px total
                                const spotlightPadding = 13;
                                const contentGap = 20; // Small gap between content and spotlight
                                const estimatedContentHeight = 320; // Estimated height for tutorial content box
                                
                                const newContentPos = {
                                top: stepData.id === 'topic-grouping'
                                    ? Math.max(100, newSpotlightPos.centerY - 250)
                                    : stepData.id === 'topic-card'
                                        ? Math.max(20, Math.min(newSpotlightPos.centerY - 200, window.innerHeight - 400)) // Vertically centered to the right
                                        : stepData.id === 'ai-tutor'
                                            ? Math.max(20, newSpotlightPos.top - spotlightPadding - contentGap - estimatedContentHeight) // Position bottom edge next to spotlight top
                                            : stepData.id === 'tip-content'
                                                ? Math.max(20, Math.min(newSpotlightPos.centerY - 200, window.innerHeight - 400)) // Vertically centered to the left
                                                : stepData.id === 'quick-actions'
                                                    ? Math.max(20, newSpotlightPos.top - spotlightPadding - contentGap - estimatedContentHeight) // Position bottom edge next to spotlight top
                                                    : stepData.id === 'tristate-buttons'
                                                        ? Math.min(newSpotlightPos.top + newSpotlightPos.height + 30, window.innerHeight - 350) // Position below the buttons
                                                        : stepData.id === 'tip-actions'
                                                        ? Math.max(20, Math.min(newSpotlightPos.centerY - 200, window.innerHeight - 400)) // Vertically centered to the left
                                                        : stepData.id === 'review-tracking'
                                                            ? Math.max(20, Math.min(newSpotlightPos.centerY - 200, window.innerHeight - 400)) // Vertically centered to the left
                                                            : stepData.id === 'new-concept-modal'
                                                                ? Math.max(20, Math.min(newSpotlightPos.centerY - 350, window.innerHeight - 520)) // Moved up, positioned to the left
                                                                : stepData.id === 'review-modal'
                                                                    ? Math.max(20, Math.min(newSpotlightPos.centerY - 250, window.innerHeight - 520)) // Vertically centered to the left
                                                                    : stepData.id === 'scheduled-generation'
                                                                        ? Math.max(20, Math.min(newSpotlightPos.centerY - 250, window.innerHeight - 520)) // Vertically centered to the left
                                                                        : stepData.id === 'ai-learning-summary'
                                                                                ? Math.max(20, Math.min(newSpotlightPos.centerY - 250, window.innerHeight - 520)) // Vertically centered to the left
                                                                                : stepData.id === 'knowledge-level-tip-panel'
                                                                                    ? Math.max(20, Math.min(newSpotlightPos.centerY - 200, window.innerHeight - 400)) // Vertically centered
                                                                                    : newSpotlightPos.top + newSpotlightPos.height + 30,
                                left: stepData.id === 'topic-grouping'
                                    ? newSpotlightPos.left + newSpotlightPos.width + 30
                                    : stepData.id === 'topic-card'
                                        ? Math.min(newSpotlightPos.left + newSpotlightPos.width + 30, window.innerWidth - 520) // Position to the RIGHT of spotlight
                                        : stepData.id === 'tip-content'
                                            ? (newSpotlightPos.left - 520 < 20 
                                                ? newSpotlightPos.left + newSpotlightPos.width + 30 // Fallback to right if no space on left
                                                : Math.max(20, newSpotlightPos.left - 520)) // Position to the LEFT of spotlight
                                            : stepData.id === 'quick-actions'
                                                ? Math.max(20, Math.min(newSpotlightPos.centerX - 250, window.innerWidth - 520)) // Center horizontally
                                                : stepData.id === 'tristate-buttons'
                                                    ? Math.max(20, Math.min(newSpotlightPos.centerX - 250, window.innerWidth - 520)) // Center horizontally below buttons
                                                    : stepData.id === 'tip-actions'
                                                    ? Math.max(20, newSpotlightPos.left - 520) // Position to the LEFT of spotlight
                                                    : stepData.id === 'review-tracking'
                                                        ? Math.max(20, newSpotlightPos.left - 520) // Position to the LEFT of spotlight
                                                        : stepData.id === 'new-concept-modal'
                                                        ? Math.max(20, newSpotlightPos.left - 520) // Position to the LEFT of modal
                                                        : stepData.id === 'review-modal'
                                                            ? Math.max(20, newSpotlightPos.left - 520) // Position to the LEFT of modal
                                                            : stepData.id === 'scheduled-generation'
                                                                ? Math.max(20, newSpotlightPos.left - 520) // Position to the LEFT of modal
                                                                : stepData.id === 'ai-learning-summary'
                                                                        ? Math.max(20, newSpotlightPos.left - 520) // Position to the LEFT of modal
                                                                        : stepData.id === 'knowledge-level-tip-panel'
                                                                            ? Math.max(20, newSpotlightPos.left - 520) // Position to the LEFT of spotlight
                                                                            : Math.max(20, Math.min(newSpotlightPos.centerX - 250, window.innerWidth - 520))
                                };
                                
                                setContentPosition(newContentPos);
                            }
                            
                            // Show content after spotlight has moved (consistent fade timing for all steps)
                            setTimeout(() => {
                                setIsTransitioning(false);
                                setShowContent(true);
                            }, 300);
                            
                            // For modal, recalculate position after animation (reduced delay)
                            if (stepData.id === 'new-concept-modal' || stepData.id === 'review-modal' || stepData.id === 'scheduled-generation' || stepData.id === 'ai-learning-summary') {
                                setTimeout(() => {
                                    const modalElement = document.querySelector(`[data-tutorial-target="${stepData.target}"]`);
                                    if (modalElement) {
                                        const rect = modalElement.getBoundingClientRect();
                                        const scrollY = window.scrollY;
                                        setSpotlightPosition({
                                            top: rect.top + scrollY,
                                            left: rect.left,
                                            width: rect.width,
                                            height: rect.height,
                                            centerX: rect.left + rect.width / 2,
                                            centerY: rect.top + rect.height / 2 + scrollY
                                        });
                                    }
                                }, 350); // Reduced delay for better performance
                            }
                        } else {
                            setIsTransitioning(false);
                            setShowContent(true);
                        }
                    }, delay);
                    
                    previousStepRef.current = stepData;
                    return () => {
                        clearTimeout(timer);
                        if (animationTimeoutRef.current) {
                            clearTimeout(animationTimeoutRef.current);
                        }
                    };
                } else {
                    // For modal type steps, center the content
                    setContentPosition({
                        top: '50%',
                        left: '50%'
                    });
                    setIsTransitioning(false);
                    setShowContent(true);
                    previousStepRef.current = stepData;
                }
            }
        } else {
            setShowContent(false);
            setSpotlightPosition(null);
            setMultipleSpotlights([]);
            setTransitionSpotlights([]);
            setIsAnimatingSpotlights(false);
            setContentPosition(null);
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
        }
    }, [isTutorialMode, isPaused, currentStep, getCurrentStepData, generateDummyContent]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
            // Clean up any pending animation frames
            setTransitionSpotlights([]);
            setIsAnimatingSpotlights(false);
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isTutorialMode) return;
            
            switch(e.key) {
                case 'Escape':
                    handleExit();
                    break;
                case 'ArrowRight':
                    if (!isPaused) handleNextStep();
                    break;
                case 'ArrowLeft':
                    if (!isPaused) handlePreviousStep();
                    break;
                default:
                    break;
            }
        };

        if (isTutorialMode) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isTutorialMode, isPaused, handleExit, handleNextStep, handlePreviousStep, pauseTutorial, resumeTutorial]);
    
    // Handle window resize to recalculate positions
    useEffect(() => {
        if (!isTutorialMode || isPaused) return;
        
        const handleResize = () => {
            const stepData = getCurrentStepData();
            if (stepData && (stepData.type === 'spotlight' || (stepData.type === 'modal' && (stepData.id === 'new-concept-modal' || stepData.id === 'review-modal' || stepData.id === 'scheduled-generation' || stepData.id === 'ai-learning-summary'))) && stepData.target) {
                // Handle multiple targets for filter-functions
                if (stepData.id === 'filter-functions') {
                    const targets = stepData.target.split(',').map(t => t.trim());
                    const spotlights = [];
                    
                    targets.forEach(target => {
                        const elements = document.querySelectorAll(`[data-tutorial-target="${target}"]`);
                        if (elements.length > 0) {
                            let minTop = Infinity, minLeft = Infinity;
                            let maxBottom = -Infinity, maxRight = -Infinity;
                            
                            elements.forEach(element => {
                                const rect = element.getBoundingClientRect();
                                const scrollY = window.scrollY;
                                
                                minTop = Math.min(minTop, rect.top + scrollY);
                                minLeft = Math.min(minLeft, rect.left);
                                maxBottom = Math.max(maxBottom, rect.bottom + scrollY);
                                maxRight = Math.max(maxRight, rect.right);
                            });
                            
                            spotlights.push({
                                top: minTop,
                                left: minLeft,
                                width: maxRight - minLeft,
                                height: maxBottom - minTop,
                                centerX: minLeft + (maxRight - minLeft) / 2,
                                centerY: minTop + (maxBottom - minTop) / 2
                            });
                        }
                    });
                    
                    if (spotlights.length > 0) {
                        setMultipleSpotlights(spotlights);
                        setSpotlightPosition(null);
                        
                        // Position content to the right of both filter areas
                        const leftSpotlight = spotlights[0];
                        const rightSpotlight = spotlights[1] || spotlights[0];
                        
                        setContentPosition({
                            top: Math.max(leftSpotlight.top + leftSpotlight.height + 30, rightSpotlight.top + rightSpotlight.height + 30),
                            left: Math.min(rightSpotlight.left + rightSpotlight.width + 30, window.innerWidth - 520)
                        });
                    }
                    return;
                }
                
                // Original single spotlight logic
                const targetElements = document.querySelectorAll(`[data-tutorial-target="${stepData.target}"]`);
                
                if (targetElements.length > 0) {
                    let minTop = Infinity, minLeft = Infinity;
                    let maxBottom = -Infinity, maxRight = -Infinity;
                    
                    targetElements.forEach((element) => {
                        const rect = element.getBoundingClientRect();
                        const scrollY = window.scrollY;
                        
                        minTop = Math.min(minTop, rect.top + scrollY);
                        minLeft = Math.min(minLeft, rect.left);
                        maxBottom = Math.max(maxBottom, rect.bottom + scrollY);
                        maxRight = Math.max(maxRight, rect.right);
                    });
                    
                    const width = maxRight - minLeft;
                    const height = maxBottom - minTop;
                    
                    const newSpotlightPos = {
                        top: minTop,
                        left: minLeft,
                        width: width,
                        height: height,
                        centerX: minLeft + width / 2,
                        centerY: minTop + height / 2
                    };
                    
                    setSpotlightPosition(newSpotlightPos);
                    
                    // Recalculate content position based on step
                    const spotlightPadding = 13;
                    const contentGap = 20;
                    const estimatedContentHeight = 320;
                    
                    const newContentPos = {
                        top: stepData.id === 'topic-grouping'
                            ? Math.max(100, newSpotlightPos.centerY - 250)
                            : stepData.id === 'topic-card'
                                ? Math.max(20, Math.min(newSpotlightPos.centerY - 200, window.innerHeight - 400))
                                : stepData.id === 'ai-tutor'
                                    ? Math.max(20, newSpotlightPos.top - spotlightPadding - contentGap - estimatedContentHeight)
                                    : stepData.id === 'tip-content'
                                        ? Math.max(20, Math.min(newSpotlightPos.centerY - 200, window.innerHeight - 400))
                                        : stepData.id === 'quick-actions'
                                            ? Math.max(20, newSpotlightPos.top - spotlightPadding - contentGap - estimatedContentHeight)
                                            : stepData.id === 'tristate-buttons'
                                                ? Math.min(newSpotlightPos.top + newSpotlightPos.height + 30, window.innerHeight - 350)
                                                : stepData.id === 'tip-actions'
                                                ? Math.max(20, Math.min(newSpotlightPos.centerY - 200, window.innerHeight - 400))
                                                : stepData.id === 'review-tracking'
                                                    ? Math.max(20, Math.min(newSpotlightPos.centerY - 200, window.innerHeight - 400))
                                                    : stepData.id === 'new-concept-modal'
                                                        ? Math.max(20, Math.min(newSpotlightPos.centerY - 350, window.innerHeight - 520))
                                                        : stepData.id === 'review-modal'
                                                            ? Math.max(20, Math.min(newSpotlightPos.centerY - 250, window.innerHeight - 520))
                                                            : stepData.id === 'scheduled-generation'
                                                                ? Math.max(20, Math.min(newSpotlightPos.centerY - 250, window.innerHeight - 520))
                                                                : stepData.id === 'ai-learning-summary'
                                                                        ? Math.max(20, Math.min(newSpotlightPos.centerY - 250, window.innerHeight - 520))
                                                                        : stepData.id === 'knowledge-level-tip-panel'
                                                                            ? Math.max(20, Math.min(newSpotlightPos.centerY - 200, window.innerHeight - 400))
                                                                            : newSpotlightPos.top + newSpotlightPos.height + 30,
                        left: stepData.id === 'topic-grouping'
                            ? newSpotlightPos.left + newSpotlightPos.width + 30
                            : stepData.id === 'topic-card'
                                ? Math.min(newSpotlightPos.left + newSpotlightPos.width + 30, window.innerWidth - 520)
                                : stepData.id === 'tip-content'
                                    ? (newSpotlightPos.left - 520 < 20 
                                        ? newSpotlightPos.left + newSpotlightPos.width + 30
                                        : Math.max(20, newSpotlightPos.left - 520))
                                    : stepData.id === 'quick-actions'
                                        ? Math.max(20, Math.min(newSpotlightPos.centerX - 250, window.innerWidth - 520))
                                        : stepData.id === 'tristate-buttons'
                                            ? Math.max(20, Math.min(newSpotlightPos.centerX - 250, window.innerWidth - 520))
                                            : stepData.id === 'tip-actions'
                                            ? Math.max(20, newSpotlightPos.left - 520)
                                            : stepData.id === 'review-tracking'
                                                ? Math.max(20, newSpotlightPos.left - 520)
                                                : stepData.id === 'new-concept-modal'
                                                ? Math.max(20, newSpotlightPos.left - 520)
                                                : stepData.id === 'review-modal'
                                                    ? Math.max(20, newSpotlightPos.left - 520)
                                                    : stepData.id === 'scheduled-generation'
                                                        ? Math.max(20, newSpotlightPos.left - 520)
                                                        : stepData.id === 'ai-learning-summary'
                                                                ? Math.max(20, newSpotlightPos.left - 520)
                                                                : stepData.id === 'knowledge-level-tip-panel'
                                                                    ? Math.max(20, newSpotlightPos.left - 520)
                                                                    : Math.max(20, Math.min(newSpotlightPos.centerX - 250, window.innerWidth - 520))
                    };
                    
                    setContentPosition(newContentPos);
                }
            }
        };
        
        // Debounce resize events for better performance
        let resizeTimer;
        const debouncedResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(handleResize, 100);
        };
        
        window.addEventListener('resize', debouncedResize);
        
        return () => {
            window.removeEventListener('resize', debouncedResize);
            clearTimeout(resizeTimer);
        };
    }, [isTutorialMode, isPaused, currentStep, getCurrentStepData]);

    if (!isTutorialMode && !isExiting) return null;

    const stepData = getCurrentStepData();
    const progress = getTutorialProgress();
    const flow = tutorialFlows[currentFlow];

    return (
        <div 
            ref={tutorialRef}
            className={`fixed inset-0 z-[70] transition-all duration-300 ${isExiting ? 'opacity-0' : 'opacity-100'}`}
        >
            {(() => {
                const isModalStep = stepData?.id === 'new-concept-modal' || stepData?.id === 'review-modal' || stepData?.id === 'scheduled-generation' || stepData?.id === 'ai-learning-summary';
                return (stepData?.type === 'spotlight' || (stepData?.type === 'modal' && isModalStep)) && (
                <>
                    <svg 
                        className="absolute inset-0 w-full h-full"
                        style={{ zIndex: 71 }}
                    >
                        <defs>
                            <mask id="tutorial-spotlight-mask">
                                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                                {/* Render transition spotlights for smooth animations */}
                                {transitionSpotlights.length > 0 && (() => {
                                    return transitionSpotlights.map((spotlight, index) => {
                                        return (
                                            <rect
                                                key={spotlight.id || `transition-${index}`}
                                                x={spotlight.left - 10}
                                                y={spotlight.top - 10}
                                                width={spotlight.width + 20}
                                                height={spotlight.height + 20}
                                                rx="12"
                                                fill="black"
                                                style={{
                                                    transition: 'all 500ms ease-out'
                                                }}
                                            />
                                        );
                                    });
                                })()}
                                {/* Render multiple spotlights for filter-functions */}
                                {multipleSpotlights.length > 0 && transitionSpotlights.length === 0 && (() => {
                                    return multipleSpotlights.map((spotlight, index) => (
                                    <rect
                                        key={index}
                                        x={spotlight.left - 10}
                                        y={spotlight.top - 10}
                                        width={spotlight.width + 20}
                                        height={spotlight.height + 20}
                                        rx="12"
                                        fill="black"
                                        className="transition-all duration-500 ease-out"
                                    />
                                    ));
                                })()}
                                {/* Single spotlight for other steps */}
                                {spotlightPosition && multipleSpotlights.length === 0 && transitionSpotlights.length === 0 && (() => {
                                    return (
                                    <rect
                                        x={spotlightPosition.left - (isModalStep ? 4 : 10)}
                                        y={spotlightPosition.top - (isModalStep ? 4 : 10)}
                                        width={spotlightPosition.width + (isModalStep ? 8 : 20)}
                                        height={spotlightPosition.height + (isModalStep ? 8 : 20)}
                                        rx={isModalStep ? "16" : "12"}
                                        fill="black"
                                        className="transition-all duration-500 ease-out"
                                    />
                                    );
                                })()}
                            </mask>
                        </defs>
                        
                        <rect 
                            x="0" 
                            y="0" 
                            width="100%" 
                            height="100%" 
                            fill="rgba(17, 24, 39, 0.75)" 
                            mask="url(#tutorial-spotlight-mask)"
                        />
                    </svg>
                    
                    {/* Render transition spotlight borders for smooth animations */}
                    {transitionSpotlights.length > 0 && transitionSpotlights.map((spotlight, index) => {
                        return (
                        <div
                            key={spotlight.id || `transition-border-${index}`}
                            className={`absolute pointer-events-none transition-all duration-500 ease-out ${isAnimatingSpotlights || showContent ? 'opacity-100' : 'opacity-0'}`}
                            style={{
                                top: spotlight.top - 10,
                                left: spotlight.left - 10,
                                width: spotlight.width + 20,
                                height: spotlight.height + 20,
                                borderRadius: '12px',
                                background: 'transparent',
                                border: '3px solid rgba(14, 165, 233, 0.8)',
                                boxShadow: '0 0 30px rgba(14, 165, 233, 0.5), inset 0 0 20px rgba(14, 165, 233, 0.3)',
                                animation: 'pulse-glow 2s ease-in-out infinite',
                                zIndex: 76
                            }}
                        />
                        );
                    })}
                    {/* Render multiple spotlight borders for filter-functions */}
                    {multipleSpotlights.length > 0 && transitionSpotlights.length === 0 && multipleSpotlights.map((spotlight, index) => (
                        <div
                            key={index}
                            className={`absolute transition-all duration-500 ease-out pointer-events-none ${showContent ? 'opacity-100' : 'opacity-0'}`}
                            style={{
                                top: spotlight.top - 10,
                                left: spotlight.left - 10,
                                width: spotlight.width + 20,
                                height: spotlight.height + 20,
                                borderRadius: '12px',
                                background: 'transparent',
                                border: '3px solid rgba(14, 165, 233, 0.8)',
                                boxShadow: '0 0 30px rgba(14, 165, 233, 0.5), inset 0 0 20px rgba(14, 165, 233, 0.3)',
                                animation: 'pulse-glow 2s ease-in-out infinite',
                                zIndex: 76
                            }}
                        />
                    ))}
                    {/* Single spotlight border for other steps */}
                    {spotlightPosition && multipleSpotlights.length === 0 && transitionSpotlights.length === 0 && (
                        <div 
                            className={`absolute transition-all duration-500 ease-out pointer-events-none ${showContent ? 'opacity-100' : 'opacity-0'}`}
                            style={{
                                top: spotlightPosition.top - (isModalStep ? 4 : 10),
                                left: spotlightPosition.left - (isModalStep ? 4 : 10),
                                width: spotlightPosition.width + (isModalStep ? 8 : 20),
                                height: spotlightPosition.height + (isModalStep ? 8 : 20),
                                borderRadius: isModalStep ? '16px' : '12px',
                                background: 'transparent',
                                border: '3px solid rgba(14, 165, 233, 0.8)',
                                boxShadow: '0 0 30px rgba(14, 165, 233, 0.5), inset 0 0 20px rgba(14, 165, 233, 0.3)',
                                animation: 'pulse-glow 2s ease-in-out infinite',
                                zIndex: 76
                            }}
                        />
                    )}
                </>
            )})()}
            
            {stepData?.type === 'modal' && stepData?.id !== 'new-concept-modal' && stepData?.id !== 'review-modal' && stepData?.id !== 'scheduled-generation' && stepData?.id !== 'ai-learning-summary' && (
                <div className="absolute inset-0 bg-gray-900/75 backdrop-blur-sm" style={{ zIndex: 71 }} />
            )}

            {((showContent || isTransitioning) && contentPosition) && (
                <div 
                    className={`absolute ${
                        showContent && !isTransitioning
                            ? 'opacity-100 scale-100' 
                            : 'opacity-0 scale-95'
                    }`}
                    style={{
                        top: contentPosition.top,
                        left: contentPosition.left,
                        transform: contentPosition.top === '50%' ? 'translate(-50%, -50%)' : 'none',
                        transition: isTransitioning ? 'opacity 0.3s, transform 0.3s' : 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        width: 500,
                        maxWidth: 'calc(100vw - 40px)',
                        zIndex: 80
                    }}
                >
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl overflow-hidden border border-gray-200/50">
                        <div className="bg-gradient-to-r from-sky-500 to-blue-600 px-6 py-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-bold text-white">
                                    {stepData?.title}
                                </h3>
                                <span className="text-sm text-white/90 font-medium">
                                    {progress.current} / {progress.total}
                                </span>
                            </div>
                        </div>
                        
                        <div className="p-6">
                            {renderStepContent(stepData, dummyContent, t, handleExit, onAddTopicClick)}
                        </div>
                        
                        <div className="bg-gradient-to-b from-gray-50 to-white px-6 py-4 flex items-center justify-between border-t border-gray-100">
                            <button
                                onClick={skipTutorial}
                                className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 hover:border-gray-300 rounded-lg transition-all duration-200 shadow-sm hover:shadow"
                            >
                                {t('tutorialSkip') || 'Skip Tutorial'}
                            </button>
                            
                            <div className="flex items-center gap-2">
                                {currentStep > 0 && (
                                    <button
                                        onClick={handlePreviousStep}
                                        className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 border border-gray-200 hover:border-gray-300 rounded-lg transition-all duration-200 flex items-center gap-2 shadow-sm hover:shadow"
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                        {t('tutorialPrevious') || 'Previous'}
                                    </button>
                                )}
                                
                                {currentStep < flow.steps.length - 1 ? (
                                    <button
                                        onClick={handleNextStep}
                                        className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold rounded-lg hover:from-sky-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                                    >
                                        {t('tutorialNext') || 'Next'}
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleExit}
                                        className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                                    >
                                        <CheckCircle className="w-4 h-4" />
                                        {t('tutorialComplete') || 'Complete'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            <button
                onClick={handleExit}
                className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110"
                style={{ zIndex: 80 }}
            >
                <X className="w-5 h-5 text-gray-600" />
            </button>
            
            <style jsx>{`
                @keyframes pulse-glow {
                    0%, 100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                    50% {
                        transform: scale(1.02);
                        opacity: 0.8;
                    }
                }
            `}</style>
        </div>
    );
};

function renderStepContent(stepData, dummyContent, t, handleExit, onAddTopicClick) {
    if (!stepData) return null;

    switch (stepData.id) {
        case 'welcome':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialWelcomeContent') || 'Welcome to your personalized learning companion! This tutorial will guide you through all the features to help you master any subject with AI-powered lessons and spaced repetition.'}
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                        <p className="text-sm text-blue-800">
                            {t('tutorialWelcomeTip') || '• This tutorial will take about 1-2 minutes to complete.'}
                        </p>
                        <p className="text-sm text-blue-700">
                            {t('tutorialNavigationTip') || '• Use ← and → arrow keys on your keyboard for easier navigation.'}
                        </p>
                    </div>
                </div>
            );

        case 'add-topic':
            // Parse text to bold specific topic examples
            const renderTipWithBoldTopics = (text) => {
                // Default text if no translation
                const tipText = text || 'Use general topics like Machine Learning, Cooking, Animals, or Gardening. You can also add learning goals and details when creating a topic to get more personalized content.';
                
                // List of topics to bold (works for multiple languages)
                const topicsToBody = ['Machine Learning', 'Cooking', 'Animals', 'Gardening', 
                                     '机器学习', '烹饪', '动物', '园艺',  // Chinese Simplified
                                     '機器學習', '烹飪', '動物', '園藝',  // Chinese Traditional  
                                     '機械学習', '料理', '動物', 'ガーデニング']; // Japanese
                
                // Split by topics and rebuild with bold
                let parts = [tipText];
                topicsToBody.forEach(topic => {
                    const newParts = [];
                    parts.forEach(part => {
                        if (typeof part === 'string' && part.includes(topic)) {
                            const splitParts = part.split(topic);
                            splitParts.forEach((splitPart, index) => {
                                if (splitPart) newParts.push(splitPart);
                                if (index < splitParts.length - 1) {
                                    newParts.push(<strong key={`${topic}-${index}`} className="font-bold italic text-blue-900">{topic}</strong>);
                                }
                            });
                        } else {
                            newParts.push(part);
                        }
                    });
                    parts = newParts;
                });
                
                return parts;
            };
            
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialAddTopicContent') || "Click the '+' button above to add your first learning topic. You can add any subject you want to master!"}
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                            <strong>{t('tutorialTip') || 'Tip:'}</strong> {renderTipWithBoldTopics(t('tutorialAddTopicTip'))}
                        </p>
                    </div>
                </div>
            );

        case 'topic-card':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialTopicCardContent') || 'Topic cards organize your learning subjects. Notice the status indicators:'}
                    </p>
                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="w-2.5 h-2.5 bg-sky-500 rounded-full mt-1.5 flex-shrink-0"></div>
                            <div>
                                <p className="font-medium text-gray-900">{t('tutorialUnreadIndicator') || 'Blue dot - Unread content'}</p>
                                <p className="text-sm text-gray-600">{t('tutorialUnreadDesc') || 'Shows when you have new tips to read'}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-2.5 h-2.5 bg-orange-500 rounded-full mt-1.5 flex-shrink-0"></div>
                            <div>
                                <p className="font-medium text-gray-900">{t('tutorialReviewIndicator') || 'Orange dot - Review due'}</p>
                                <p className="text-sm text-gray-600">{t('tutorialReviewDesc') || 'Time to review concepts for better retention'}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <svg className="w-3.5 h-3.5 text-red-500 mt-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
                            </svg>
                            <div>
                                <p className="font-medium text-gray-900">{t('tutorialFavoriteIndicator') || 'Heart - Favorite topic'}</p>
                                <p className="text-sm text-gray-600">{t('tutorialFavoriteDesc') || 'Your starred topics for quick access'}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-3">
                        <p className="text-sm text-amber-800">
                            <strong>{t('tutorialInteractionHint') || 'Quick Actions:'}</strong> {t('tutorialTopicInteractionDesc') || 'Right-click on any topic for more options like marking as favorite, editing, or deleting.'}
                        </p>
                    </div>
                </div>
            );

        case 'topic-grouping':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialTopicGroupingContent') || 'Keep your topics organized with custom groups! Group related topics together for better organization and workflow.'}
                    </p>
                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-sky-600 font-semibold text-xs">1</span>
                            </div>
                            <div>
                                <p className="font-medium text-gray-900">{t('tutorialCreateGroup') || 'Create Topic Groups'}</p>
                                <p className="text-sm text-gray-600">{t('tutorialCreateGroupDesc') || 'Click "Add Group" to create custom categories like "Work Projects", "Personal Learning", or "Hobbies"'}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-sky-600 font-semibold text-xs">2</span>
                            </div>
                            <div>
                                <p className="font-medium text-gray-900">{t('tutorialDragTopics') || 'Drag Topics into Groups'}</p>
                                <p className="text-sm text-gray-600">{t('tutorialDragTopicsDesc') || 'Simply drag and drop topic cards into any group to organize them'}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-sky-600 font-semibold text-xs">3</span>
                            </div>
                            <div>
                                <p className="font-medium text-gray-900">{t('tutorialReorderWithinGroups') || 'Reorder Topics Within Groups'}</p>
                                <p className="text-sm text-gray-600">{t('tutorialReorderWithinGroupsDesc') || 'Drag topics up and down within a group to arrange them in your preferred order'}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-sky-600 font-semibold text-xs">4</span>
                            </div>
                            <div>
                                <p className="font-medium text-gray-900">{t('tutorialReorderGroups') || 'Reorder Entire Groups'}</p>
                                <p className="text-sm text-gray-600">{t('tutorialReorderGroupsDesc') || 'Drag the group header to reorder entire groups and prioritize your learning areas'}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <p className="text-sm text-purple-800">
                            <strong>{t('tutorialGroupingTip') || 'Organization Tip:'}</strong> {t('tutorialGroupingTipDesc') || 'Groups help you focus on specific areas during your learning sessions and keep related topics together for better context.'}
                        </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-3">
                        <p className="text-sm text-green-800">
                            <strong>{t('tutorialGroupingNote') || 'Important Note:'}</strong> {t('tutorialGroupingNoteDesc') || "Groups are purely for visual organization and won't affect the AI-generated content in any way. They simply help you better organize your topic cards."}
                        </p>
                    </div>
                </div>
            );

        case 'add-tip':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialAddTipContent') || 'Click the "+" button in the tips panel to generate a new AI-powered learning concept. This may take up to 1 minute as the AI creates personalized content for you.'}
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-sm text-green-800">
                            <strong>{t('tutorialManualTipBenefit') || 'Pro Tip:'}</strong> {t('tutorialManualTipDesc') || "Don't want to wait? You can schedule concept generation in the topic settings, so concepts are prepared for you ahead of time automatically!"}
                        </p>
                    </div>
                </div>
            );

        case 'tip-card':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialTipCardContent') || 'These are your learning concepts. Click any tip to view its full content, start an AI conversation, or mark it for review.'}
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="text-sm text-amber-800">
                            <strong>{t('tutorialInteractionHint') || 'Quick Actions:'}</strong> {t('tutorialInteractionDesc') || 'Right-click on any tip for more options like marking as favorite, editing, or deleting.'}
                        </p>
                    </div>
                </div>
            );

        case 'new-concept-modal':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialNewConceptModalIntro')}
                    </p>
                    <div className="space-y-4">
                        <div className="border-l-4 border-sky-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialTooEasy')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialTooEasyDesc')}</p>
                        </div>
                        <div className="border-l-4 border-green-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialAlreadyKnown')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialAlreadyKnownDesc')}</p>
                        </div>
                        <div className="border-l-4 border-yellow-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialPartiallyKnow')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialPartiallyKnowDesc')}</p>
                        </div>
                        <div className="border-l-4 border-rose-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialNewToMe')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialNewToMeDesc')}</p>
                        </div>
                        <div className="border-l-4 border-purple-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialTooHard')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialTooHardDesc')}</p>
                        </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
                        <p className="text-sm text-blue-800">
                            <strong>{t('tutorialKnowledgeTip')}</strong> {t('tutorialKnowledgeTipDesc')}
                        </p>
                    </div>
                </div>
            );

        case 'knowledge-level-tip-panel':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialKnowledgeLevelPanelIntro')}
                    </p>
                    <div className="space-y-4">
                        <div className="border-l-4 border-green-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialProgressTracking')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialProgressTrackingDesc')}</p>
                        </div>
                        <div className="border-l-4 border-orange-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialFlexibleLearning')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialFlexibleLearningDesc')}</p>
                        </div>
                    </div>
                </div>
            );

        case 'tip-content':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialTipContentIntro')}
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
                        <p className="text-sm text-blue-800">
                            <strong>{t('tutorialScrollTip')}</strong> {t('tutorialScrollTipDesc')}
                        </p>
                    </div>
                </div>
            );

        case 'ai-tutor':
            if (dummyContent?.type === 'conversation') {
                return (
                    <div className="space-y-4">
                        <p className="text-gray-700 leading-relaxed">
                            {t('tutorialAITutorContent')}
                        </p>
                        {dummyContent.data.messages.length > 0 && (
                            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                {dummyContent.data.messages.map((msg, i) => (
                                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[80%] rounded-lg p-3 ${
                                            msg.role === 'user' 
                                                ? 'bg-sky-500 text-white' 
                                                : 'bg-white border border-gray-200'
                                        }`}>
                                            <p className="text-sm">{msg.content}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            }
            break;

        case 'quick-actions':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialQuickActionsIntro')}
                    </p>
                </div>
            );

        case 'tristate-buttons':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialViewModesIntro')}
                    </p>
                    <div className="space-y-4">
                        <div className="border-l-4 border-amber-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialTipOnlyMode')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialTipOnlyModeDesc')}</p>
                        </div>
                        <div className="border-l-4 border-sky-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialFullViewMode')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialFullViewModeDesc')}</p>
                        </div>
                        <div className="border-l-4 border-purple-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialChatOnlyMode')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialChatOnlyModeDesc')}</p>
                        </div>
                    </div>
                </div>
            );

        case 'review-tracking':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialReviewTrackingIntro')}
                    </p>
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialDateLearned')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialDateLearnedDesc')}</p>
                        </div>
                        <div className="border-l-4 border-orange-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialReviewButton')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialReviewButtonDesc')}</p>
                        </div>
                        <div className="border-l-4 border-green-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialDaysTillReview')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialDaysTillReviewDesc')}</p>
                        </div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-3">
                        <p className="text-sm text-purple-800">
                            <strong>{t('tutorialSpacedRepetition') || 'Spaced Repetition Science:'}</strong> {t('tutorialSpacedRepetitionDesc') || 'This proven learning technique spaces out review sessions at increasing intervals, moving knowledge from short-term to long-term memory. Studies show this can improve retention by up to 200%!'}
                        </p>
                    </div>
                </div>
            );
        
        case 'tip-actions':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialTipActionsIntro')}
                    </p>
                    <div className="space-y-4">
                        <div className="border-l-4 border-gray-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialHideAction')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialHideActionDesc')}</p>
                        </div>
                        <div className="border-l-4 border-red-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialFavoriteAction')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialFavoriteActionDesc')}</p>
                        </div>
                        <div className="border-l-4 border-blue-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialScheduleReview')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialScheduleReviewDesc')}</p>
                        </div>
                    </div>
                </div>
            );

        case 'review-modal':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialReviewModalIntro')}
                    </p>
                    <div className="space-y-4">
                        <div className="border-l-4 border-orange-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialAnswerQuestions')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialAnswerQuestionsDesc')}</p>
                        </div>
                        <div className="border-l-4 border-blue-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialDeepDiveChat')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialDeepDiveChatDesc')}</p>
                        </div>
                        <div className="border-l-4 border-green-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialProgressTracking')}</p>
                            <p className="text-sm text-gray-600">{t('tutorialProgressTrackingDesc')}</p>
                        </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-3">
                        <p className="text-sm text-amber-800">
                            <strong>{t('tutorialActiveRecall')}</strong> {t('tutorialActiveRecallDesc')}
                        </p>
                    </div>
                </div>
            );

        case 'review-system':
            if (dummyContent?.type === 'review') {
                return (
                    <div className="space-y-4">
                        <p className="text-gray-700 leading-relaxed">
                            {t('tutorialReviewContent')}
                        </p>
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-3">{dummyContent.data.question}</h4>
                            <div className="space-y-2">
                                {dummyContent.data.options.map((option, i) => (
                                    <button
                                        key={i}
                                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                                            i === dummyContent.data.correctAnswer
                                                ? 'border-green-400 bg-green-50'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        <span className="text-sm">{option}</span>
                                    </button>
                                ))}
                            </div>
                            {dummyContent.data.explanation && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                                    <p className="text-sm text-blue-800">
                                        <strong>{t('tutorialExplanation') || 'Explanation:'}</strong> {dummyContent.data.explanation}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
            break;

        case 'scheduled-generation':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialScheduledGenerationIntro') || 'Automate your learning with scheduled concept generation! Set it and forget it - new concepts will be generated automatically at your preferred time.'}
                    </p>
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialSetSchedule') || 'Set Your Schedule'}</p>
                            <p className="text-sm text-gray-600">{t('tutorialSetScheduleDesc') || 'Choose when and how often new concepts should be generated - daily, weekly, or monthly at your preferred time.'}</p>
                        </div>
                        <div className="border-l-4 border-green-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialTimezoneAware') || 'Timezone Aware'}</p>
                            <p className="text-sm text-gray-600">{t('tutorialTimezoneAwareDesc') || 'The system automatically adjusts to your timezone, ensuring concepts are ready when you expect them.'}</p>
                        </div>
                        <div className="border-l-4 border-amber-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialSmartSkipping') || 'Smart Skipping'}</p>
                            <p className="text-sm text-gray-600">{t('tutorialSmartSkippingDesc') || 'Enable "Skip if unread concepts exist" to prevent overwhelming yourself with too many unread concepts.'}</p>
                        </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
                        <p className="text-sm text-blue-800">
                            <strong>{t('tutorialTip') || 'Tip:'}</strong> {t('tutorialRightClickTopicTip') || 'You can access these settings anytime by right-clicking on any topic card and selecting "Edit".'}
                        </p>
                    </div>
                </div>
            );

        case 'filter-functions':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialFilterFunctionsIntro') || 'Stay organized with smart filtering! Quickly find the topics and concepts that matter most to you with powerful filter options.'}
                    </p>
                    <div className="space-y-4">
                        <div className="border-l-4 border-red-200 pl-4">
                            <p className="font-medium text-gray-900">
                                <span className="inline-flex items-center gap-1">
                                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                    {t('tutorialFavoriteFilter') || 'Favorites Filter'}
                                </span>
                            </p>
                            <p className="text-sm text-gray-600">{t('tutorialFavoriteFilterDesc') || 'Click the heart icon to show only your favorite topics or concepts. Perfect for focusing on content you care about most.'}</p>
                        </div>
                        <div className="border-l-4 border-yellow-200 pl-4">
                            <p className="font-medium text-gray-900">
                                <span className="inline-flex items-center gap-1">
                                    <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {t('tutorialReviewDueFilter') || 'Review Due Filter'}
                                </span>
                            </p>
                            <p className="text-sm text-gray-600">{t('tutorialReviewDueFilterDesc') || 'Click the clock icon to show only items due for review. Stay on top of your spaced repetition schedule.'}</p>
                        </div>
                        <div className="border-l-4 border-purple-200 pl-4">
                            <p className="font-medium text-gray-900">
                                <span className="inline-flex items-center gap-1">
                                    <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                    </svg>
                                    {t('tutorialAdvancedFilter') || 'Advanced Filters'}
                                </span>
                            </p>
                            <p className="text-sm text-gray-600">{t('tutorialAdvancedFilterDesc') || 'Click the funnel icon to access advanced filtering options like status (unread, marked as known, etc.) and scheduled generation settings.'}</p>
                        </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-3">
                        <p className="text-sm text-amber-800">
                            <strong>{t('tutorialFilterProTip') || 'Pro Tip:'}</strong> {t('tutorialCombineFiltersTip') || 'You can combine multiple filters to create powerful views. For example, show only favorite topics that are due for review!'}
                        </p>
                    </div>
                </div>
            );

        case 'ai-learning-summary':
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t('tutorialAILearningSummaryIntro') || 'Get personalized insights into your learning journey! The AI analyzes your interactions to understand your knowledge profile, learning patterns, and optimization opportunities.'}
                    </p>
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialKnowledgeProfile') || 'Knowledge Profile'}</p>
                            <p className="text-sm text-gray-600">{t('tutorialKnowledgeProfileDesc') || 'Understand your strengths and knowledge gaps based on your engagement with concepts and conversations.'}</p>
                        </div>
                        <div className="border-l-4 border-green-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialLearningPatterns') || 'Learning Patterns'}</p>
                            <p className="text-sm text-gray-600">{t('tutorialLearningPatternsDesc') || 'Discover how you learn best - whether through examples, theory, or practice-based approaches.'}</p>
                        </div>
                        <div className="border-l-4 border-purple-200 pl-4">
                            <p className="font-medium text-gray-900">{t('tutorialOptimizationInsights') || 'Optimization Insights'}</p>
                            <p className="text-sm text-gray-600">{t('tutorialOptimizationInsightsDesc') || 'Get actionable recommendations to enhance your learning effectiveness and retention.'}</p>
                        </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-3">
                        <p className="text-sm text-amber-800">
                            <strong>{t('tutorialRequirementNote') || 'Note:'}</strong> {t('tutorialRequirementNoteDesc') || 'You need to read at least 5 concepts before generating your first AI learning summary. This ensures enough data for meaningful insights.'}
                        </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
                        <p className="text-sm text-blue-800">
                            <strong>{t('tutorialTip') || 'Tip:'}</strong> {t('tutorialRightClickTopicTip') || 'You can access these settings anytime by right-clicking on any topic card and selecting "Edit".'}
                        </p>
                    </div>
                </div>
            );

        case 'complete':
            return (
                <div className="space-y-4">
                    <div className="text-center py-4">
                        <h4 className="text-2xl font-bold text-gray-900 mb-3">
                            {t('tutorialCongrats') || 'Congratulations!'}
                        </h4>
                        <p className="text-gray-700 text-base leading-relaxed">
                            {t('tutorialCompleteContent') || "You've completed the tutorial! You're now ready to start your learning journey."}
                        </p>
                    </div>
                    <div className="flex justify-center mt-6">
                        <button
                            onClick={() => {
                                handleExit();
                                if (onAddTopicClick) {
                                    setTimeout(() => onAddTopicClick(), 300);
                                }
                            }}
                            className="px-8 py-4 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold text-lg rounded-xl hover:from-sky-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-3"
                        >
                            <Plus className="w-6 h-6" />
                            <span>{t('addYourFirstTopic') || 'Add Your First Topic'}</span>
                        </button>
                    </div>
                    <p className="text-center text-sm text-gray-500 mt-4">
                        {t('tutorialStartLearningHint') || 'Start by adding a topic you\'re passionate about learning!'}
                    </p>
                </div>
            );

        default:
            return (
                <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                        {t(`tutorial${stepData.id}Content`) || 'Learn about this feature...'}
                    </p>
                </div>
            );
    }
}

export default TutorialMode;